import { useCallback, useRef, useState } from "react";

const LIMIT = 100;

// Undo/redo for the canvas element list.
//
// `set(next, mergeKey)` — edits that share a mergeKey within MERGE_MS collapse
// into one undo step, so typing in a text box or nudging with the arrow keys
// undoes as a whole instead of one character / one 0.5mm at a time.
// `reset(list)` replaces the list and clears history (template load).
const MERGE_MS = 900;

export default function useCanvasHistory(initial = []) {
  const [state, setState] = useState({ past: [], present: initial, future: [] });
  const lastMerge = useRef({ key: null, at: 0 });

  const set = useCallback((nextOrFn, mergeKey) => {
    setState((st) => {
      const next = typeof nextOrFn === "function" ? nextOrFn(st.present) : nextOrFn;
      if (next === st.present) return st;
      const now = Date.now();
      const merge =
        mergeKey &&
        lastMerge.current.key === mergeKey &&
        now - lastMerge.current.at < MERGE_MS &&
        st.past.length > 0;
      lastMerge.current = { key: mergeKey || null, at: now };
      return {
        past: merge ? st.past : [...st.past, st.present].slice(-LIMIT),
        present: next,
        future: [],
      };
    });
  }, []);

  const undo = useCallback(() => {
    lastMerge.current = { key: null, at: 0 };
    setState((st) =>
      st.past.length
        ? { past: st.past.slice(0, -1), present: st.past[st.past.length - 1], future: [st.present, ...st.future] }
        : st,
    );
  }, []);

  const redo = useCallback(() => {
    lastMerge.current = { key: null, at: 0 };
    setState((st) =>
      st.future.length
        ? { past: [...st.past, st.present], present: st.future[0], future: st.future.slice(1) }
        : st,
    );
  }, []);

  const reset = useCallback((list) => {
    lastMerge.current = { key: null, at: 0 };
    setState({ past: [], present: list, future: [] });
  }, []);

  return {
    elements: state.present,
    set,
    undo,
    redo,
    reset,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
